// Ét tick af simulationen. Rækkefølgen er fast, så samme input altid giver samme state.

import { getLevel } from '../content/levels';
import { apply } from './actions';
import { growCity, newCityGraceTicks, growthPulse, updateClocks, updateDemand } from './cities';
import { autoDispatch, updateCrews } from './crews';
import { difficulty, greenGoal } from './difficulty';
import { autoPackIndex, incomeDrip, yearendAutoTicks } from './economy';
import { updateDirector } from './events';
import { CASCADE_LEFT_TICKS, computeFlow, endEmergencies, FLOW_MIN_INTERVAL, inEmergency, lineArea, markDirty, OVERLOAD_TRIP_TICKS } from './flow';
import { checkNewLine, degree, nodeById, updateShields } from './grid';
import { updateHz } from './hz';
import { nextInt } from './rng';
import { updatePowers } from './powers';
import { greenPct, updateAvailability } from './state';
import { breakLine, updateThreats } from './threats';
import { releaseSmaa, updateSmaa } from './smaa';
import type { GridNode, Intent, LevelDef, SimEvent, State } from './types';
import { SEASON_TICKS, TICK_HZ, YEAR_TICKS } from './types';
import { seasonOf } from './weather';
import { endYear, pickPack } from './year';

/** Banens samlede længde i banetid (ticks). */
export function levelTicks(level: LevelDef): number {
  return level.years * YEAR_TICKS + level.finaleS * TICK_HZ;
}

function activate(state: State, n: GridNode, events: SimEvent[]): void {
  n.active = true;
  n.activeSince = state.tick;
  n.appearedAt = state.clockTick;
  if (n.city) {
    n.city.graceTicks = newCityGraceTicks(difficulty(state.difficulty), state.year);
    n.city.grewAt = state.clockTick;
  }
  markDirty(state);
  events.push({ t: 'node_appeared', tick: state.tick, node: n.id, kind: n.kind });
  if (n.kind === 'city') connectionFee(state, n, events);
}

/**
 * Tilslutningsbidrag: når næste mørklægning ville slutte banen (tillid 1 eller mindre), og kassen ikke
 * rækker til den billigste luftledning ud til en ny by, betaler byen selv det, der mangler. Kassen
 * fyldes op til netop den pris, ikke mere, så en tom kasse aldrig alene gør en ny by til et sikkert
 * tab (det skete især på Orkan, med én tillid). Med mere tillid tilbage er det spillerens egen sag.
 */
function connectionFee(state: State, n: GridNode, events: SimEvent[]): void {
  if (state.trust > 1) return;
  let cheapest = Infinity;
  for (const m of state.nodes) {
    if (!m.active || m.id === n.id || m.phantom) continue;
    if (!m.producer && degree(state, m.id) === 0) continue;
    const c = checkNewLine(state, n.id, m.id, 'air', () => true);
    if (c.ok) cheapest = Math.min(cheapest, c.cost);
  }
  if (!Number.isFinite(cheapest)) return;
  const amount = cheapest - state.budget;
  if (amount <= 0) return;
  state.budget += amount;
  events.push({ t: 'connection_fee', tick: state.tick, node: n.id, amount });
}

/**
 * Idempotent på (spiller, clientSeq): en intent med en sekvens, der ikke er højere end den sidst
 * udførte, udføres aldrig (igen). Den forsvinder heller ikke i stilhed: den kommer tilbage som
 * `rejected` med `duplicate`, så afsenderen ved, at netop det forsøg ikke gjorde noget. Klienten
 * sender i rækkefølge over én forbindelse, så en lavere sekvens betyder et gensendt forsøg.
 */
function applyIntents(state: State, intents: readonly Intent[], events: SimEvent[]): void {
  for (const intent of intents) {
    const last = state.lastSeq[intent.player] ?? 0;
    if (intent.clientSeq <= last) {
      events.push({ t: 'rejected', tick: state.tick, player: intent.player, kind: intent.kind, error: 'duplicate' });
      continue;
    }
    state.lastSeq[intent.player] = intent.clientSeq;
    const r = apply(state, intent, events);
    if (!r.ok) events.push({ t: 'rejected', tick: state.tick, player: intent.player, kind: intent.kind, error: r.error });
  }
}

function scheduled(state: State, events: SimEvent[]): void {
  let shields = false;
  while (state.pendingSites.length > 0 && (state.pendingSites[0]?.at ?? Infinity) <= state.clockTick) {
    const s = state.pendingSites.shift() as { node: string; at: number };
    const n = nodeById(state, s.node);
    if (n && !n.active) {
      activate(state, n, events);
      shields = true;
    }
  }
  while (state.pendingGrowth.length > 0 && (state.pendingGrowth[0]?.at ?? Infinity) <= state.clockTick) {
    const g = state.pendingGrowth.shift() as State['pendingGrowth'][number];
    const n = nodeById(state, g.node);
    if (n && n.active) growCity(state, n, g.tier, events);
  }
  for (const b of state.pendingBoosts) {
    if (b.at === state.clockTick) {
      const n = nodeById(state, b.node);
      if (n?.producer) {
        n.producer.boostMw = b.mw;
        n.producer.boostTicks = b.ticks;
        markDirty(state);
      }
    }
  }
  for (const n of state.nodes) {
    if (n.producer && n.producer.boostTicks > 0) {
      n.producer.boostTicks--;
      if (n.producer.boostTicks === 0) {
        n.producer.boostMw = 0;
        markDirty(state);
      }
    }
  }
  // Ingen ny kunde midt i en storm: hele planen venter, til stormen er drevet over. Playtest runde 2:
  // Aarhus dukkede op under et stormvarsel, og to udfald i samme sekund afgjorde spillet.
  const stormy = state.pool.length > 0 && state.threats.some((t) => t.kind === 'storm' && t.phase !== 'done');
  if (stormy && state.clockTick >= state.nextCustomerAt) {
    for (let i = 0; i < state.customerPlan.length; i++) state.customerPlan[i] = (state.customerPlan[i] as number) + 1;
    state.nextCustomerAt = state.customerPlan[0] ?? Number.MAX_SAFE_INTEGER;
  }
  pullAhead(state);
  // Kunder fra puljen: én ad gangen efter planen (tre i sværhedens tempo, resten spredt over banen).
  if (state.pool.length > 0 && state.clockTick >= state.nextCustomerAt) {
    const id = state.pool.shift() as string;
    const n = nodeById(state, id);
    if (n && !n.active) {
      const pct = difficulty(state.difficulty).phantomPct;
      const roll = nextInt(state.rng.customers, 0, 99);
      // Kun datacentre kan være fantomer: møller, solparker og værker findes altid.
      if (n.kind === 'datacenter' && roll < pct) n.phantom = true;
      activate(state, n, events);
      shields = true;
    }
    state.customerPlan.shift();
    state.nextCustomerAt = state.customerPlan[0] ?? Number.MAX_SAFE_INTEGER;
  }
  if (shields) updateShields(state);
}

/** Kunder før tid kræver mindst så mange kroner i kassen, så den nye kunde kan nås. */
export const PULL_MIN_BUDGET = 20;

/**
 * Er nettet i ro: ingen trusler, ingen brud, alle byer fuldt forsynet med tomt ur, og penge nok til at
 * tage imod en ny kunde?
 */
function calmGrid(state: State): boolean {
  if (state.phase !== 'playing' || state.threats.length > 0 || state.budget < PULL_MIN_BUDGET) return false;
  if (state.lines.some((l) => l.status === 'broken' || l.halfDown)) return false;
  for (const n of state.nodes) {
    const c = n.city;
    if (!c || !n.active || n.phantom) continue;
    if (c.clock > 0 || c.servedMw * 100 < c.demandMw * 98) return false;
  }
  return true;
}

/**
 * Kunder før tid: på Lærling og Vagthavende kommer den næste kunde, når nettet har været i ro i
 * `pullAheadS` sekunder. Playtest runde 3: Lærling havde 20-30 s, hvor der intet var at gøre, og
 * "Næste år"-knappen blev det eneste træk. Kun den næste kunde rykkes; resten af planen står.
 */
function pullAhead(state: State): void {
  const s = difficulty(state.difficulty).pullAheadS ?? 0;
  if (s <= 0 || state.pool.length === 0) return;
  if (!calmGrid(state)) {
    state.calmCustomerTicks = 0;
    return;
  }
  const calm = (state.calmCustomerTicks ?? 0) + 1;
  state.calmCustomerTicks = calm;
  if (calm < s * TICK_HZ) return;
  if (state.nextCustomerAt - state.clockTick <= s * TICK_HZ) return;
  state.calmCustomerTicks = 0;
  state.customerPlan[0] = state.clockTick;
  state.nextCustomerAt = state.clockTick;
}

function batteries(state: State): void {
  for (const n of state.nodes) {
    const b = n.battery;
    if (!b || b.usedMw === 0) continue;
    const before = b.store;
    b.store = Math.max(0, Math.min(b.storeMax, b.store - b.usedMw));
    if ((before > 0 && b.store === 0) || (before < b.storeMax && b.store === b.storeMax)) markDirty(state);
  }
}

export function overloads(state: State, events: SimEvent[]): void {
  const n1 = difficulty(state.difficulty).n1;
  const trips = n1 === 'required' || n1 === 'cascades';
  for (const line of state.lines) {
    if (line.status !== 'ok' || line.loadPm <= 1000) {
      line.overTicks = 0;
      continue;
    }
    line.overTicks++;
    if (line.overTicks === 1) events.push({ t: 'line_overload', tick: state.tick, line: line.id, loadPm: line.loadPm });
    if (trips && line.overTicks >= OVERLOAD_TRIP_TICKS && !inEmergency(state, lineArea(state, line))) {
      breakLine(state, line, 'overload', events);
      // Ægte kaskader: de andre ledninger over 100 % har nu kun 2 s til at slå fra.
      if (n1 === 'cascades') {
        for (const o of state.lines) {
          if (o !== line && o.status === 'ok' && o.loadPm > 1000) o.overTicks = Math.max(o.overTicks, OVERLOAD_TRIP_TICKS - CASCADE_LEFT_TICKS);
        }
      }
    }
  }
}

function energyStats(state: State): void {
  const f = state.flow;
  const ys = state.yearStats;
  ys.servedSum += f.servedMw;
  ys.greenSum += f.greenMw;
  ys.exportSum += f.exportMw;
  ys.ticks++;
  state.stats.greenSum += f.greenMw;
  state.stats.servedSum += f.servedMw;
  const bucket = Math.floor(state.clockTick / TICK_HZ) % 60;
  if (state.clockTick % TICK_HZ === 0) {
    state.stats.greenRing[bucket] = 0;
    state.stats.totalRing[bucket] = 0;
  }
  state.stats.greenRing[bucket] = (state.stats.greenRing[bucket] ?? 0) + f.greenMw;
  state.stats.totalRing[bucket] = (state.stats.totalRing[bucket] ?? 0) + f.servedMw;
}

/** Den tredje stjerne: ingen by må have været mere end 40 % inde i sit mørkeur. Et brud, der
 *  bliver lappet efter stormen, koster den; en mast, der blev forstærket i tide, gør ikke. */
const STAR_CLOCK_PM = 400;

/** Fra Vagthavende skal byer fra denne størrelse (7 huse) have to veje ind for den tredje stjerne. */
export const STAR_N1_TIER = 2;

/**
 * Hvad der kostede en stjerne, så resultatskærmen kan sige det præcist: 'green' (grønt mål),
 * 'customer' (en kunde fik aldrig en ledning), 'clock' (en by ventede for længe) eller 'n1' (en by
 * hang i én ledning). null = tre stjerner.
 */
export type StarMiss = { why: 'green' } | { why: 'customer' | 'clock' | 'n1'; node: string } | null;

export function starMiss(state: State, level: LevelDef): StarMiss {
  const g = greenPct(state);
  if (g < greenGoal(level.greenGoalPct, state.difficulty)) return { why: 'green' };
  const n1 = difficulty(state.difficulty).n1;
  const realPool = level.sites.filter((s) => s.at === 'pool').map((s) => s.node);
  // Kun kunder, der faktisk dukkede op, tæller: at kalde året tidligt må ikke koste en stjerne.
  for (const id of realPool) {
    const n = nodeById(state, id);
    if (!n || n.phantom || !n.active) continue;
    // En station er et værktøj, ikke en kunde: den koster ikke stjernen (IGN runde 3: Trige på bane 4).
    if (n.kind === 'station') continue;
    // Enhver ledning tæller, også en knækket: en storm, der tog byens eneste ledning, er et ur-problem,
    // ikke en kunde, der "aldrig kom på nettet" (playtest runde 3).
    if (!state.lines.some((l) => l.a === id || l.b === id)) return { why: 'customer', node: id };
  }
  for (const n of state.nodes) {
    if (!n.city || !n.active || n.phantom) continue;
    if (n.city.maxClockPm > STAR_CLOCK_PM) return { why: 'clock', node: n.id };
  }
  for (const n of state.nodes) {
    if (!n.city || !n.active || n.phantom || n.shield >= 2) continue;
    // Vagthavende: de store byer skal have to veje ind. Det giver kassen et formål i de rolige
    // perioder (playtest runde 2: "pengene hober sig op"). Systemansvarlig og Orkan: alle byer.
    if (n1 === 'required' || n1 === 'cascades') return { why: 'n1', node: n.id };
    if (n1 === 'bonus' && n.kind === 'city' && n.city.tier >= STAR_N1_TIER) return { why: 'n1', node: n.id };
  }
  return null;
}

/** Stjerner ved sejr. */
export function stars(state: State, level: LevelDef): 1 | 2 | 3 {
  const m = starMiss(state, level);
  return m === null ? 3 : m.why === 'green' ? 1 : 2;
}

function finish(state: State, level: LevelDef, won: boolean, reason: string, events: SimEvent[]): void {
  const g = greenPct(state);
  if (won) {
    const s = stars(state, level);
    state.phase = 'won';
    state.result = { won: true, stars: s, greenPct: g, reason };
    events.push({ t: 'level_won', tick: state.tick, stars: s, greenPct: g });
  } else {
    state.phase = 'lost';
    state.result = { won: false, stars: 0, greenPct: g, reason };
    events.push({ t: 'level_lost', tick: state.tick, reason });
  }
}

/**
 * Udfør intents uden at tiden går (pause, årsskifte-skærm). Samme vej som `step`: idempotent på
 * (spiller, clientSeq), afvisninger kommer som `rejected`-hændelser. Room-DO og Volt bruger den også.
 */
export function applyNow(state: State, intents: readonly Intent[]): SimEvent[] {
  const events: SimEvent[] = [];
  if (state.phase === 'won' || state.phase === 'lost') return events;
  applyIntents(state, intents, events);
  return events;
}

/** Kør ét tick med de givne intents. Returnerer hændelserne fra tikket. */
export function step(state: State, intents: readonly Intent[] = []): SimEvent[] {
  const events: SimEvent[] = [];
  if (state.phase === 'won' || state.phase === 'lost') return events;
  const level = getLevel(state.levelId);

  applyIntents(state, intents, events);
  state.tick++;

  if (state.phase === 'yearend') {
    state.phaseTicks++;
    if (state.phaseTicks >= yearendAutoTicks(state)) {
      // Med roller: har én af jer stemt, er det den gave, der tages (ikke timerens).
      const v = state.packVotes;
      const voted = v ? (v.p1 ?? v.p2) : undefined;
      const by = v?.p1 !== undefined ? 'p1' : 'p2';
      if (voted !== undefined) pickPack(state, level, voted, false, events, by);
      else pickPack(state, level, autoPackIndex(state, level), true, events);
    }
    return events;
  }
  if (state.phase !== 'playing') return events;

  state.clockTick++;
  if (state.clockTick % SEASON_TICKS === 0) {
    events.push({ t: 'season', tick: state.tick, season: seasonOf(state.clockTick), year: state.year });
    markDirty(state);
  }
  incomeDrip(state, events);

  scheduled(state, events);
  growthPulse(state, events);
  updatePowers(state, events);
  endEmergencies(state);
  updateAvailability(state);
  updateDemand(state, level);
  updateThreats(state, events);
  updateCrews(state, events);
  autoDispatch(state, events);
  updateDirector(state, level, events);
  updateSmaa(state, level, events);
  updateHz(state, events);

  if (state.flow.dirty && state.tick - state.flow.lastTick >= FLOW_MIN_INTERVAL) {
    computeFlow(state);
    // En gæst på en ledning, der fik travlt, går, før byernes ure ser flowet.
    if (releaseSmaa(state, events)) computeFlow(state);
  }
  batteries(state);
  overloads(state, events);
  updateClocks(state, level, events);
  energyStats(state);

  if (state.trust <= 0) {
    finish(state, level, false, 'trust', events);
    return events;
  }
  if (state.finale) {
    if (state.clockTick >= levelTicks(level)) finish(state, level, true, 'cleared', events);
    return events;
  }
  if (state.clockTick % YEAR_TICKS === 0 && state.year < level.years) endYear(state, level, events);
  return events;
}

/** Kør n ticks uden intents (til test og hurtig spoling). */
export function run(state: State, ticks: number): SimEvent[] {
  const all: SimEvent[] = [];
  for (let i = 0; i < ticks; i++) {
    const ev = step(state);
    for (const e of ev) all.push(e);
    if (state.phase === 'won' || state.phase === 'lost') break;
  }
  return all;
}
